import { Link } from 'react-router-dom';
import { seoConfig } from '../../configs/seoConfig';
import { useSiteConfig } from '../../configs/siteConfig';
import LegalLayout, { LegalContact } from '../terms/LegalLayout';

const PrivacyPolicyPage = () => {
  const { companyName, phone } = useSiteConfig().contactInfo;
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How we collect, use and protect your information."
      seo={seoConfig.pages.privacy}
    >
      <h2>1. Introduction</h2>
      <p>
        Welcome to {companyName} ("we", "our", or "us"). This Privacy Policy
        explains how we collect, use, disclose, and safeguard your information
        when you visit our website and purchase our digital products. Please read
        this privacy policy carefully. If you do not agree with the terms of this
        privacy policy, please do not access the site.
      </p>

      <h2>2. Information We Collect</h2>
      <p>
        We collect information that you provide directly to us when you use our
        services. The types of information we may collect include:
      </p>

      <h3>2.1 Personal information</h3>
      <ul>
        <li>
          <strong>Email address:</strong> for your account and for messages
          about your orders
        </li>
        <li>
          <strong>Phone number:</strong> for order verification and customer
          support
        </li>
        <li>
          <strong>Name:</strong> for personalization and order processing
        </li>
        <li>
          <strong>GitHub username:</strong> to give you access to a project's
          GitHub repository
        </li>
        <li>
          <strong>Payment details:</strong> the UPI transaction ID (and, if you
          provide one, a payment screenshot link) you submit so we can verify
          your payment. You pay from your own UPI app, so we never receive your
          UPI PIN, card details or bank login.
        </li>
      </ul>

      <h3>2.2 Messages you send us</h3>
      <p>
        When you use the <Link to="/contact">Contact</Link> or{' '}
        <Link to="/request-project">Request a project</Link> forms, we store your
        name, email address, optional phone number and your message so we can
        reply and keep track of your request.
      </p>

      <h3>2.3 Automatically collected information</h3>
      <ul>
        <li>IP address and browser information</li>
        <li>Device information and operating system</li>
        <li>Pages visited and time spent on our website</li>
        <li>Referral source and exit pages</li>
        <li>Cookies and similar technologies</li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <p>We use the information we collect for the following purposes:</p>
      <ul>
        <li>
          <strong>Order processing:</strong> to process and deliver your digital
          product purchases
        </li>
        <li>
          <strong>Communication:</strong> to send order updates and to reply to
          your messages and support requests
        </li>
        <li>
          <strong>Account management:</strong> to create and manage your user
          account
        </li>
        <li>
          <strong>Payment verification:</strong> to match your UPI payment to
          your order
        </li>
        <li>
          <strong>Service improvement:</strong> to analyze usage patterns and
          improve our website and services
        </li>
        <li>
          <strong>Security:</strong> to detect, prevent, and address technical
          issues and fraudulent activities
        </li>
        <li>
          <strong>Marketing:</strong> to send promotional materials and updates
          (you can opt out at any time)
        </li>
      </ul>

      <h2>4. Order Processing Time</h2>
      <div className="pk-alert" data-tone="info">
        <p>
          <strong>Important notice:</strong> After a successful payment, orders
          are typically processed within <strong>12 hours</strong>. Once your
          order is processed, you can access the product from your order page.
        </p>
        <p>
          During this time we verify your payment and set up your access. If your
          order takes longer than 12 hours, please contact us.
        </p>
      </div>

      <h2>5. Data Sharing and Disclosure</h2>
      <p>
        We do not sell, trade, or rent your personal information to third
        parties. We may share your information only in the following
        circumstances:
      </p>
      <ul>
        <li>
          <strong>Service providers:</strong> with trusted third-party service
          providers who help us operate our website and process payments (for
          example hosting, payment and email services)
        </li>
        <li>
          <strong>GitHub:</strong> your GitHub username, when we invite you to a
          project's repository
        </li>
        <li>
          <strong>Legal requirements:</strong> when required by law, regulation,
          or legal process
        </li>
        <li>
          <strong>Business transfers:</strong> in connection with any merger,
          sale, or acquisition of our business
        </li>
        <li>
          <strong>With your consent:</strong> when you have given us explicit
          permission to share your information
        </li>
      </ul>

      <h2>6. Data Security</h2>
      <p>
        We implement appropriate technical and organizational security measures
        to protect your personal information against unauthorized access,
        alteration, disclosure, or destruction. These measures include:
      </p>
      <ul>
        <li>HTTPS encryption for data sent between your browser and our servers</li>
        <li>Passwords stored as one-way hashes, never in plain text</li>
        <li>Access controls and authentication for our admin tools</li>
      </ul>
      <p>
        However, please note that no method of transmission over the Internet or
        electronic storage is 100% secure. While we strive to use commercially
        acceptable means to protect your information, we cannot guarantee its
        absolute security.
      </p>

      <h2>7. Data Retention</h2>
      <p>
        We retain your personal information for as long as necessary to fulfill
        the purposes outlined in this privacy policy, unless a longer retention
        period is required by law. Specifically:
      </p>
      <ul>
        <li>Account information is retained while your account is active</li>
        <li>
          Transaction records are kept for a minimum of 7 years for accounting
          and legal purposes
        </li>
        <li>Marketing communications data is retained until you opt out</li>
        <li>Analytics data is typically retained for 24-36 months</li>
      </ul>

      <h2>8. Your Rights</h2>
      <p>You have the following rights regarding your personal data:</p>
      <ul>
        <li>
          <strong>Access:</strong> request a copy of the personal information we
          hold about you
        </li>
        <li>
          <strong>Correction:</strong> request correction of inaccurate or
          incomplete data
        </li>
        <li>
          <strong>Deletion:</strong> request deletion of your personal data
          (subject to legal obligations)
        </li>
        <li>
          <strong>Opt-out:</strong> unsubscribe from marketing communications at
          any time
        </li>
        <li>
          <strong>Data portability:</strong> request a copy of your data in a
          portable format
        </li>
        <li>
          <strong>Objection:</strong> object to certain processing of your
          personal information
        </li>
      </ul>
      <p>
        To exercise any of these rights, contact us through the{' '}
        <Link to="/contact">Contact page</Link> or on WhatsApp at{' '}
        {phone.display}.
      </p>

      <h2>9. Cookies and Similar Technologies</h2>
      <p>
        We use cookies and similar technologies, such as your browser's local
        storage, to run the website and understand how it is used. We use:
      </p>
      <ul>
        <li>
          <strong>Essential storage:</strong> required for website functionality
          and security, such as keeping you signed in
        </li>
        <li>
          <strong>Analytics cookies:</strong> Google Analytics, to understand
          how visitors use our site
        </li>
        <li>
          <strong>Preference storage:</strong> to remember your settings and
          preferences
        </li>
      </ul>
      <p>
        You can control cookies through your browser settings, but disabling
        certain cookies may affect website functionality.
      </p>

      <h2>10. Third-Party Links</h2>
      <p>
        Our website may contain links to third-party websites, such as GitHub and
        YouTube. We are not responsible for the privacy practices or content of
        these external sites. We encourage you to review the privacy policies of
        any third-party sites you visit.
      </p>

      <h2>11. Children's Privacy</h2>
      <p>
        Our services are not intended for individuals under the age of 18. We do
        not knowingly collect personal information from children. If you believe
        we have collected information from a child, please contact us
        immediately so we can delete such information.
      </p>

      <h2>12. International Data Transfers</h2>
      <p>
        Your information may be transferred to and processed in countries other
        than your country of residence. These countries may have different data
        protection laws. By using our services, you consent to such transfers.
      </p>

      <h2>13. Changes to This Privacy Policy</h2>
      <p>
        We may update this privacy policy from time to time to reflect changes in
        our practices or legal requirements. We will notify you of any material
        changes by posting the new privacy policy on this page and updating the
        "Last updated" date. We encourage you to review this privacy policy
        periodically.
      </p>

      <h2>14. Contact Us</h2>
      <p>
        If you have any questions, concerns, or requests regarding this privacy
        policy or our data practices, contact us:
      </p>
      <LegalContact />

      <h2>15. Consent</h2>
      <p>
        By using our website and services, you consent to our privacy policy and
        agree to its terms. If you do not agree with this policy, please do not
        use our services.
      </p>
    </LegalLayout>
  );
};

export default PrivacyPolicyPage;
